import { useCallback } from 'react';
import type { SelectOptionProp } from '@ui/SelectField/types';
import type {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue
} from './types';

export function postFilterChangeDependants(
  item: DataTableProviderFilterItem,
  newState: Map<string, DataTableProviderFilterStateValue>
) {
  if (item.dependants) {
    item.dependants.forEach((filterKey) => {
      newState.set(filterKey, {
        item: item,
        value: '',
        label: ''
      });
    });
  }
}

export function useDatatableFilterDraftHandlers(
  filtersState: Map<string, DataTableProviderFilterStateValue>,
  setFiltersState: React.Dispatch<
    React.SetStateAction<Map<string, DataTableProviderFilterStateValue>>
  >
) {
  const handleFilterInputChange = useCallback(
    (item: DataTableProviderFilterItem) =>
      (value: string | number | string[]) => {
        const pattern = /^\d+(\.\d{1,2})?$/;

        const newState = new Map(filtersState);
        let newValue = value;
        if (
          item.type === 'number' &&
          item.filterKey === 'amount' &&
          !pattern.test(String(value)) &&
          value !== ''
        )
          return;
        if (item.type === 'number') {
          if (item.moreThan) {
            const refValue =
              filtersState.get(item.moreThan) &&
              Number(filtersState.get(item.moreThan)?.value);

            if (refValue && Number(newValue) < refValue) {
              newValue = refValue;
            }
          }
          if (item.minValue && Number(newValue) < item.minValue) {
            newValue = item.minValue;
          }
          if (!item.allowNegative && Number(newValue) < 0) {
            newValue = 0;
          }

          if (item.lessThan) {
            const refValue =
              filtersState.get(item.lessThan) &&
              Number(filtersState.get(item.lessThan)?.value);

            if (refValue && Number(newValue) > refValue) {
              newValue = refValue;
            }
          }
          if (item.maxValue && Number(newValue) < item.maxValue) {
            newValue = item.maxValue;
          }
        }
        newState.set(item.filterKey, {
          item: item,
          value: newValue,
          label: value
        });
        postFilterChangeDependants(item, newState);
        setFiltersState(newState);
      },
    [filtersState, setFiltersState]
  );

  const handleFilterSelectChange = useCallback(
    (item: DataTableProviderFilterItem) =>
      (value: string | number | string[], option?: SelectOptionProp) => {
        const selectedOption =
          option || item.selectOptions?.find((i) => `${i.value}` === `${value}`);
        const newState = new Map(filtersState);

        if (
          item.multiSelect &&
          (item.type === 'choice_chips' || item.type === 'select')
        ) {
          const strValue = String(value ?? '');
          const currentRaw = String(
            filtersState.get(item.filterKey)?.value ?? ''
          );
          let nextIds = currentRaw
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);

          if (strValue === '') {
            nextIds = [];
          } else if (nextIds.includes(strValue)) {
            nextIds = nextIds.filter((id) => id !== strValue);
          } else {
            nextIds = [...nextIds, strValue];
          }

          const previousLabels = String(
            filtersState.get(item.filterKey)?.label ?? ''
          )
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);
          const previousIds = currentRaw
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);
          const labelById = new Map(
            previousIds.map((id, index) => [id, previousLabels[index] ?? id])
          );
          if (option && strValue) {
            labelById.set(strValue, String(option.label ?? strValue));
          }

          const nextValue = nextIds.join(',');
          const labels = nextIds
            .map(
              (id) =>
                item.selectOptions?.find((opt) => String(opt.value) === id)
                  ?.label ??
                labelById.get(id) ??
                id
            )
            .join(', ');

          newState.set(item.filterKey, {
            item,
            value: nextValue,
            label: labels
          });
        } else {
          newState.set(item.filterKey, {
            item: item,
            value: value,
            label: selectedOption?.label || value
          });
        }

        if (item.onChange) item.onChange(option ? option : value);
        postFilterChangeDependants(item, newState);
        setFiltersState(newState);
      },
    [filtersState, setFiltersState]
  );

  const handleAutoCompleteChange = useCallback(
    (item: DataTableProviderFilterItem) =>
      (value: string | number | string[], option?: SelectOptionProp) => {
        const newState = new Map(filtersState);
        newState.set(item.filterKey, {
          item: item,
          value: value,
          label: option?.label || value
        });
      postFilterChangeDependants(item, newState);
      setFiltersState(newState);
    },
    [filtersState, setFiltersState]
  );

  return {
    handleFilterInputChange,
    handleFilterSelectChange,
    handleAutoCompleteChange
  };
}
